import { usePersistentState } from '../../core/usePersistentState.ts';
// frontend/src/modules/comment-monitor/useCommentMonitor.ts
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { api, ApiClientError } from '../../core/apiClient.ts';
import { useToast } from '../../core/contexts/ToastContext.tsx';

export interface CommentWithStatus {
  id: string;
  postId: string;
  message: string | null;
  fromId: string | null;
  fromName: string | null;
  createdTime: string | null;
  isHidden: boolean;
  isRead: boolean;
  likeCount: number;
  syncedAt: string;
}

export interface PostWithCommentStats {
  id: string;
  pageId: string;
  pageName: string | null;
  message: string | null;
  pictureUrl: string | null;
  fullPicture: string | null;
  permalinkUrl: string | null;
  createdTime: string | null;
  totalComments: number;
  unreadComments: number;
  hiddenComments: number;
  isMonitored: boolean;
  groupName?: string;
}

export function useCommentMonitor() {
  const { showToast } = useToast();
  const [posts, setPosts] = useState<PostWithCommentStats[]>([]);
  const [loading, setLoading] = useState(false);
  const [syncProgress, setSyncProgress] = useState<{current: number; total: number} | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [filterGroupId, setFilterGroupId] = usePersistentState<number | null>('monitor.group', null);
  const [sortBy, setSortBy] = usePersistentState<string>('monitor.sort', 'unread_comments_desc');

  const [openedPost, setOpenedPost] = useState<string | null>(null);
  const [comments, setComments] = useState<CommentWithStatus[]>([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [autoRefreshMinutes, setAutoRefreshMinutes] = useState<number | null>(() => {
    const saved = localStorage.getItem('fbdash_refresh_minutes');
    return saved ? Number(saved) : null;
  });
  const [countdownSeconds, setCountdownSeconds] = useState<number | null>(null);

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (filterGroupId) params.append('group_id', filterGroupId.toString());
      if (sortBy) params.append('sort_by', sortBy);
      
      const data = await api.get<PostWithCommentStats[]>(`/api/comment-monitor/posts?${params.toString()}`);
      setPosts(data);
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Не вдалося завантажити пости');
    } finally {
      setLoading(false);
    }
  }, [filterGroupId, sortBy]);

  const isSyncingRef = React.useRef(false);

  const syncAll = useCallback(async (isBackground = false) => {
    if (isSyncingRef.current) return;
    isSyncingRef.current = true;
    if (!isBackground) setLoading(true);
    
    try {
      const postsToSync = posts.filter(p => p.isMonitored);
      if (postsToSync.length === 0) {
        if (!isBackground) showToast('Немає постів для моніторингу', 'info');
        return;
      }

      setSyncProgress({ current: 0, total: postsToSync.length });
      let successCount = 0;
      let failCount = 0;

      for (let i = 0; i < postsToSync.length; i++) {
        setSyncProgress({ current: i + 1, total: postsToSync.length });
        try {
          await api.post(`/api/comment-monitor/sync/${postsToSync[i].id}`);
          successCount++;
        } catch(e) {
          failCount++;
        }
        
        if (i < postsToSync.length - 1) {
          await new Promise(r => setTimeout(r, 5000));
        }
      }

      await fetchPosts();
      
      if (failCount === 0) {
        showToast(`Успішно оновлено ${successCount}/${postsToSync.length} постів`, 'success');
      } else {
        showToast(`Оновлено ${successCount}/${postsToSync.length} постів. Помилок: ${failCount}`, 'error');
      }
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Помилка синхронізації');
    } finally {
      if (!isBackground) setLoading(false);
      setSyncProgress(null);
      isSyncingRef.current = false;
      // Restart countdown after sync is completely finished
      if (autoRefreshMinutes) {
        setCountdownSeconds(autoRefreshMinutes * 60);
      }
    }
  }, [posts, fetchPosts, autoRefreshMinutes, showToast]);

  useEffect(() => {
    if (autoRefreshMinutes && autoRefreshMinutes > 0) {
      localStorage.setItem('fbdash_refresh_minutes', autoRefreshMinutes.toString());
      setCountdownSeconds(autoRefreshMinutes * 60);
    } else {
      localStorage.removeItem('fbdash_refresh_minutes');
      setCountdownSeconds(null);
    }
  }, [autoRefreshMinutes]);

  useEffect(() => {
    if (!autoRefreshMinutes || autoRefreshMinutes <= 0) return;

    const interval = setInterval(() => {
      if (isSyncingRef.current) return; // Pause countdown

      setCountdownSeconds((prev) => {
        if (prev === null) return null;
        if (prev <= 1) {
          syncAll(true);
          return 0; // Hold at 0 while syncing
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [autoRefreshMinutes, syncAll]);

  const syncOne = useCallback(async (postId: string) => {
    try {
      await api.post(`/api/comment-monitor/sync/${postId}`);
      await fetchPosts();
    } catch (err) {
      console.error('Помилка синхронізації поста:', err);
    }
  }, [fetchPosts]);

  const openPost = useCallback(async (postId: string) => {
    setOpenedPost(postId);
    setCommentsLoading(true);
    try {
      const data = await api.get<CommentWithStatus[]>(`/api/comment-monitor/posts/${postId}/comments`);
      setComments(data);
      // Оновити список постів у фоні, щоб скинути лічильник нечитаних
      fetchPosts();
    } catch (err) {
      console.error('Помилка завантаження коментарів:', err);
    } finally {
      setCommentsLoading(false);
    }
  }, [fetchPosts]);

  const hideComment = useCallback(async (commentId: string) => {
    try {
      await api.post(`/api/comment-monitor/comments/${commentId}/hide`);
      // Оптимістично оновлюємо статус в UI
      setComments(prev => prev.map(c => c.id === commentId ? { ...c, isHidden: true, isRead: true } : c));
      fetchPosts(); // Оновлюємо статистику
      return { ok: true };
    } catch (err) {
      console.error('Помилка приховання коментаря:', err);
      return { ok: false, error: err instanceof Error ? err.message : 'Unknown error' };
    }
  }, [fetchPosts]);

  const [isHidingMultiple, setIsHidingMultiple] = useState(false);

  const hideMultipleComments = useCallback(async (commentIds: string[]) => {
    if (commentIds.length === 0) return;
    setIsHidingMultiple(true);
    let successCount = 0;
    let failCount = 0;

    for (let i = 0; i < commentIds.length; i++) {
      const cid = commentIds[i];
      const res = await hideComment(cid);
      if (res.ok) successCount++;
      else failCount++;
      
      if (i < commentIds.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 2000));
      }
    }

    if (failCount === 0) {
      showToast(`Успішно приховано ${successCount}/${commentIds.length} коментарів.`, 'success');
    } else {
      showToast(`Приховано ${successCount}/${commentIds.length}. Помилок: ${failCount}`, 'error');
    }
    
    setIsHidingMultiple(false);
  }, [hideComment, showToast]);

  const readComment = useCallback(async (commentId: string) => {
    try {
      await api.post(`/api/comment-monitor/comments/${commentId}/read`);
      // Оптимістично оновлюємо статус в UI
      setComments(prev => prev.map(c => c.id === commentId ? { ...c, isRead: true } : c));
      fetchPosts(); // Оновлюємо статистику
    } catch (err) {
      console.error('Помилка позначення коментаря як прочитаного:', err);
      alert('Не вдалося позначити коментар як прочитаний');
    }
  }, [fetchPosts]);

  const toggleMonitor = useCallback(async (postId: string, isMonitored: boolean) => {
    // Оптимістично оновлюємо UI
    setPosts(prev => prev.map(p => p.id === postId ? { ...p, isMonitored } : p));
    try {
      await api.post(`/api/comment-monitor/posts/${postId}/toggle-monitor`, { isMonitored });
    } catch (err) {
      console.error('Помилка зміни статусу моніторингу:', err);
      // Відкат
      setPosts(prev => prev.map(p => p.id === postId ? { ...p, isMonitored: !isMonitored } : p));
      alert('Не вдалося змінити статус моніторингу');
    }
  }, []);

  const closePost = useCallback(() => {
    setOpenedPost(null);
    setComments([]);
  }, []);

  return {
    posts,
    loading,
    error,
    fetchPosts,
    syncAll,
    syncOne,
    openedPost,
    comments,
    commentsLoading,
    openPost,
    closePost,
    hideComment,
    hideMultipleComments,
    isHidingMultiple,
    readComment,
    toggleMonitor,
    autoRefreshMinutes,
    setAutoRefreshMinutes,
    countdownSeconds,
    filterGroupId,
    setFilterGroupId,
    sortBy,
    setSortBy,
    syncProgress,
  };
}
